import type { monaco } from 'react-monaco-editor';
import type Dataset from '@/services/dataset';
import { history } from 'umi';
import DatasetTest from '@/services/datasetTest';
import { handleUnknownError } from './handleError';

export const beautifyName = (s: string) => {
  let name = s.split('_');
  name = name.map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return name.join(' ');
};

/**
 * Make the column's name more readable
 * From "order_no_" to "Order No"
 * @param {*} columns
 */
export const beautifyNames = (columns: { title: string }[]) =>
  columns.map((c) => ({
    ...c,
    title: beautifyName(c.title),
  }));

/**
 * to get the "bare" url without the "view/edit" or "version"
 * ie. /hub/datasets/8f35583b-faf5-4acf-80b3-a244e018eafc
 * not /hub/datasets/8f35583b-faf5-4acf-80b3-a244e018eafc/view/latest
 * not /hub/datasets/8f35583b-faf5-4acf-80b3-a244e018eafc/edit/latest
 * not /hub/datasets/8f35583b-faf5-4acf-80b3-a244e018eafc/view/1234567890
 * not /hub/datasets/8f35583b-faf5-4acf-80b3-a244e018eafc/edit/1234567890
 */
const regexs = [
  /^(.*\/hub)\/?/, // this regex must always be the first one
  /^(.*)\/(view|edit)\/\d{10}\/?$/,
  /^(.*)\/(view|edit)\/latest\/?$/,
  /^(.*)\/(view|edit)\/?$/,
  /^(.*)\/$/,
];
export const getViewLatestUrl = (record: Dataset | DatasetTest) => {
  const { pathname } = history.location;
  const orgWsPrefix = pathname.split('/').slice(0, 3).join('/');
  const currentPath = pathname.substring(orgWsPrefix.length);
  let result;
  regexs.find((re, i) => {
    const matches = currentPath.match(re);
    if (!matches) return false;
    [result] = matches.slice(1, 2);
    if (i === 0) {
      const seg = record instanceof DatasetTest ? 'test/' : '';
      result = `${result}/datasets/${seg}${record.id}`;
    }
    return true;
  });
  if (result) return orgWsPrefix + result;
  return pathname;
};

/**
 * to redirect the page to view or edit a specific version or the latest version
 */
export const redirectTo = (
  record: Dataset | DatasetTest,
  mode: 'view' | 'edit',
  version: string,
  reload = true,
) => {
  const url = getViewLatestUrl(record);
  history.push(`${url}/${mode}/${version}`);
  if (reload) window.location.reload();
};

export const addOrgWsPrefix = (path: string): string => {
  const { pathname } = history.location;
  const orgWsPrefix = pathname.split('/').slice(0, 3).join('/');
  return orgWsPrefix + path;
};

export const selectedTextInEditor = (editor: monaco.editor.IStandaloneCodeEditor | null) => {
  try {
    let text;
    if (editor) {
      const editorSelections = editor.getSelections();
      if (editorSelections) {
        // join text from all selections
        text = editorSelections
          .map((selection) => editor.getModel()?.getValueInRange(selection))
          .join(' ');

        if (text === '') {
          text = editor.getModel()?.getValue();
        }
      }
    }
    return text;
  } catch (e) {
    handleUnknownError(e);
    return '';
  }
};
